import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, UploadCloud, Link as LinkIcon, LogOut, ImageIcon, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia } from "@/lib/api";
import { setAdminToken } from "@/lib/adminState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState({ total_videos: 0 });

  // form state
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState("url");
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");
  const [busy, setBusy] = useState(false);

  // Change password form
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!oldPw || !newPw) {
      toast.error("Fill both fields");
      return;
    }
    if (newPw.length < 4) {
      toast.error("New password must be at least 4 characters");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/admin/change-password", {
        old_password: oldPw,
        new_password: newPw,
      });
      toast.success("Password updated");
      setOldPw("");
      setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setPwBusy(false);
    }
  };

  const reload = async () => {
    const [v, s] = await Promise.all([
      api.get("/videos", { params: { sort: "latest", limit: 200 } }),
      api.get("/admin/stats"),
    ]);
    setVideos(v.data.items);
    setStats(s.data);
  };

  useEffect(() => {
    const token = localStorage.getItem("xr_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    api
      .get("/admin/verify")
      .then(() => {
        api.get("/categories").then((r) => {
          setCategories(r.data);
          if (r.data.length > 0) setCategory(r.data[0].slug);
        });
        reload();
      })
      .catch(() => {
        setAdminToken("");
        navigate("/admin");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    setAdminToken("");
    navigate("/admin");
  };

  const onThumbFile = (f) => {
    setThumbFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setThumbPreview(url);
    } else {
      setThumbPreview("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !category) {
      toast.error("Title and category are required");
      return;
    }
    setBusy(true);
    try {
      let finalVideoUrl = "";
      let finalThumbUrl = "";

      // Resolve video
      if (mode === "url") {
        if (!videoUrl.trim()) {
          toast.error("Provide a video URL");
          setBusy(false);
          return;
        }
        finalVideoUrl = videoUrl.trim();
      } else {
        if (!videoFile) {
          toast.error("Choose a video file");
          setBusy(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", videoFile);
        const r = await api.post("/upload/video", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        finalVideoUrl = r.data.url;
      }

      // Resolve thumbnail
      if (thumbFile) {
        const fd = new FormData();
        fd.append("file", thumbFile);
        const r = await api.post("/upload/thumbnail", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        finalThumbUrl = r.data.url;
      } else if (thumbnailUrl.trim()) {
        finalThumbUrl = thumbnailUrl.trim();
      } else {
        toast.error("Provide a thumbnail (file or URL)");
        setBusy(false);
        return;
      }

      await api.post("/videos", {
        title: title.trim(),
        category,
        video_url: finalVideoUrl,
        thumbnail_url: finalThumbUrl,
        source_type: mode === "url" ? "url" : "upload",
      });

      toast.success("Video published");
      setTitle("");
      setVideoUrl("");
      setThumbnailUrl("");
      setVideoFile(null);
      setThumbFile(null);
      setThumbPreview("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const removeVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`);
    toast.success("Deleted");
    await reload();
  };

  const clearDemo = async () => {
    if (!window.confirm("Remove ALL demo videos?")) return;
    const r = await api.post("/admin/clear-demo");
    toast.success(`Removed ${r.data.deleted} demo videos`);
    await reload();
  };
  void clearDemo; // kept for the API; UI button removed by request

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-12 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3 md:mb-10 md:gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#e60039] md:text-xs">
            Curator Console
          </div>
          <h1
            data-testid="admin-dashboard-title"
            className="mt-1 font-display text-3xl tracking-tight text-[#f2f2f2] sm:text-4xl md:mt-2 md:text-5xl"
          >
            Admin Dashboard
          </h1>
          <p className="mt-1 text-xs text-[#a3a3a3] md:mt-2 md:text-sm">
            {stats.total_videos} {stats.total_videos === 1 ? "video" : "videos"}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="btn-admin-home"
            onClick={() => navigate("/")}
            aria-label="Visit site"
            title="View site as admin"
            className="inline-flex items-center gap-2 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#f2f2f2] transition-colors hover:border-[#e60039] hover:text-[#e60039] md:px-4 md:text-xs"
          >
            <HomeIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Site</span>
          </button>
          <button
            data-testid="btn-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#f2f2f2] transition-colors hover:bg-[#1a1a1a] md:px-4 md:text-xs"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[420px_1fr] lg:gap-10">
        {/* Left column: Upload + Change Password */}
        <div className="space-y-6">
        {/* Upload form */}
        <form
          onSubmit={submit}
          data-testid="upload-form"
          className="rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] p-6"
        >
          <h2 className="font-display text-2xl text-[#f2f2f2]">Publish a video</h2>
          <div className="mt-5 space-y-4">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                Title
              </label>
              <input
                data-testid="upload-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title..."
                className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] focus:border-[#e60039] focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                Category
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="upload-category"
                  className="rounded-sm border-[#1a1a1a] bg-[#0f0f0f] text-[#f2f2f2]"
                >
                  <SelectValue placeholder="Choose..." />
                </SelectTrigger>
                <SelectContent className="border-[#1a1a1a] bg-[#0a0a0a] text-[#f2f2f2]">
                  {categories.map((c) => (
                    <SelectItem
                      key={c.slug}
                      value={c.slug}
                      data-testid={`upload-cat-${c.slug}`}
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                Video source
              </label>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="grid grid-cols-2 rounded-sm bg-[#0f0f0f]">
                  <TabsTrigger
                    value="url"
                    data-testid="tab-mode-url"
                    className="rounded-sm data-[state=active]:bg-[#e60039] data-[state=active]:text-white"
                  >
                    <LinkIcon className="mr-2 h-3.5 w-3.5" /> URL
                  </TabsTrigger>
                  <TabsTrigger
                    value="file"
                    data-testid="tab-mode-file"
                    className="rounded-sm data-[state=active]:bg-[#e60039] data-[state=active]:text-white"
                  >
                    <UploadCloud className="mr-2 h-3.5 w-3.5" /> Local file
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="url" className="mt-3">
                  <input
                    data-testid="upload-video-url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://...mp4"
                    className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] focus:border-[#e60039] focus:outline-none"
                  />
                </TabsContent>
                <TabsContent value="file" className="mt-3">
                  <input
                    data-testid="upload-video-file"
                    type="file"
                    accept="video/*"
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                    className="block w-full cursor-pointer rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#a3a3a3] file:mr-4 file:rounded-sm file:border-0 file:bg-[#1a1a1a] file:px-3 file:py-1.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-[#f2f2f2] hover:file:bg-[#e60039]"
                  />
                  {videoFile && (
                    <p className="mt-2 text-xs text-[#a3a3a3]">
                      {videoFile.name} · {(videoFile.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                Thumbnail (file or URL)
              </label>
              <input
                data-testid="upload-thumb-file"
                type="file"
                accept="image/*"
                onChange={(e) => onThumbFile(e.target.files?.[0] || null)}
                className="mb-2 block w-full cursor-pointer rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#a3a3a3] file:mr-4 file:rounded-sm file:border-0 file:bg-[#1a1a1a] file:px-3 file:py-1.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-[#f2f2f2] hover:file:bg-[#e60039]"
              />
              <input
                data-testid="upload-thumb-url"
                value={thumbnailUrl}
                onChange={(e) => setThumbnailUrl(e.target.value)}
                placeholder="...or paste thumbnail URL"
                className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] focus:border-[#e60039] focus:outline-none"
              />
              {(thumbPreview || thumbnailUrl) && (
                <div className="mt-3 aspect-video w-full overflow-hidden rounded-sm border border-[#1a1a1a]">
                  {thumbPreview ? (
                    <img src={thumbPreview} alt="preview" className="h-full w-full object-cover" />
                  ) : (
                    <img src={thumbnailUrl} alt="preview" className="h-full w-full object-cover" />
                  )}
                </div>
              )}
              {!thumbPreview && !thumbnailUrl && (
                <div className="mt-3 flex aspect-video w-full items-center justify-center rounded-sm border border-dashed border-[#1a1a1a] text-[#666]">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
            </div>

            <button
              type="submit"
              data-testid="upload-submit"
              disabled={busy}
              className="w-full rounded-sm bg-[#e60039] py-3 text-sm font-medium uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#b3002c] disabled:opacity-50"
            >
              {busy ? "Publishing..." : "Publish video"}
            </button>
          </div>
        </form>

        {/* Change password */}
        <form
          onSubmit={submitPassword}
          data-testid="change-password-form"
          className="rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] p-6"
        >
          <h2 className="font-display text-2xl text-[#f2f2f2]">Change password</h2>
          <div className="mt-5 space-y-3">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                Current password
              </label>
              <input
                data-testid="pw-old"
                type="password"
                autoComplete="current-password"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] focus:border-[#e60039] focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-[0.24em] text-[#666]">
                New password
              </label>
              <input
                data-testid="pw-new"
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-3 py-2 text-sm text-[#f2f2f2] focus:border-[#e60039] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="pw-submit"
              disabled={pwBusy}
              className="w-full rounded-sm bg-[#e60039] py-2.5 text-sm font-medium uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#b3002c] disabled:opacity-50"
            >
              {pwBusy ? "Updating..." : "Update password"}
            </button>
          </div>
        </form>
        </div>

        {/* Videos list */}
        <div>
          <h2 className="mb-5 font-display text-2xl text-[#f2f2f2]">All videos</h2>
          <div data-testid="admin-video-list" className="space-y-3">
            {videos.length === 0 && (
              <div className="rounded-sm border border-dashed border-[#1a1a1a] p-10 text-center text-[#666]">
                No videos yet.
              </div>
            )}
            {videos.map((v) => (
              <div
                key={v.id}
                className="flex items-center gap-3 rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] p-2.5 md:gap-4 md:p-3"
              >
                <div className="aspect-video w-24 shrink-0 overflow-hidden rounded-sm bg-black md:w-32">
                  <img
                    src={resolveMedia(v.thumbnail_url)}
                    alt={v.title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-base text-[#f2f2f2] md:text-lg">
                      {v.title}
                    </h3>
                    {v.is_demo && (
                      <span className="rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] text-[#a3a3a3] md:px-2 md:text-[10px]">
                        Demo
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-[10px] uppercase tracking-[0.18em] text-[#666] md:text-xs">
                    {v.category} · {v.views} views
                  </p>
                </div>
                <button
                  data-testid={`btn-delete-${v.id}`}
                  onClick={() => removeVideo(v.id)}
                  className="rounded-sm p-2 text-[#666] transition-colors hover:bg-[#1a1a1a] hover:text-[#e60039]"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
